import React, { useState, useEffect } from 'react';
import { 
  useParams, 
  useNavigate, 
  Routes, 
  Route, 
  Navigate, 
  useLocation 
} from 'react-router-dom';
import { formatWhatsApp, isValidEmail } from '../utils/formatUtils';
import { isValidDateBR, formatDateBR, convertDateBRToISO, convertMinutesToTimeFormat, convertTimeFormatToMinutes, formatDate, formatarHorario } from '../utils/dateUtils';
import { formatarPreco } from '../utils/priceUtils';
import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  deleteDoc,
  addDoc,
  Timestamp,
  doc as firestoreDoc
} from 'firebase/firestore';
import {
  getSalonConfig,
  updateSalonConfig,
  getAppointments,
  confirmAppointment as confirmAppointmentFB,
  cancelAppointment as cancelAppointmentFB,
  markAppointmentMessageSent
} from '../firebase';
import { canUserManageUsers } from '../firebase/userRoles';
import SalonConfig from '../components/SalonConfig';
import ProfessionalAvailability from '../components/ProfessionalAvailability';
import CategoryManager from '../components/CategoryManager';
import CommissionManager from '../components/CommissionManager';
import AppointmentTimeline from '../components/booking/timeline/AppointmentTimeline';
import { DateInput } from '../components/ui';

// Importação dos componentes existentes
import AdminNavigation from '../components/admin/AdminNavigation';
import AdminHeader from '../components/AdminHeader';
import AdminFloatingActionButton from '../components/AdminFloatingActionButton';

// Importar o novo componente de confirmações pendentes
import ConfirmationsPage from '../components/admin/ConfirmationsPage';

// Importar o novo componente de gerenciamento de serviços
import ServiceManagement from '../components/admin/ServiceManagement';

// Importar o novo componente de gerenciamento de profissionais
import ProfessionalManagement from '../components/admin/ProfessionalManagement';

// Importar o componente AdminDashboard
import AdminDashboard from '../components/admin/AdminDashboard';

// Importar o componente SettingsManagement
import SettingsManagement from '../components/admin/SettingsManagement';

// Importar o componente de gerenciamento de usuários
import UserManagement from '../components/admin/UserManagement';

// Importar o componente de gerenciamento de convites
import InvitationManager from '../components/admin/InvitationManager';

// Importar os componentes modulares para os modais
import AppointmentModal from '../components/AppointmentModal';

import { NavigationProvider, useNavigation } from '../contexts/NavigationContext';
import { useNotificationCounts } from '../hooks/useNotificationCounts';

// Importar o novo componente de gerenciamento de agendamentos
import AppointmentManagement from '../components/admin/AppointmentManagement';

// Importar o novo componente de agendamento móvel
import MobileBookingForm from '../components/mobile/MobileBookingForm';

import { useAppointments } from '../contexts/AppointmentContext';
import { useServices } from '../contexts/ServiceContext';
import { useProfessionals } from '../contexts/ProfessionalContext';
import { useUser } from '../contexts/UserContext';

// Importar o componente PerformancePage
import PerformancePage from '../components/performance/PerformancePage';

// Importar o novo componente de agendamento móvel
import AdminMobileAppointmentForm from '../components/admin/MobileAppointmentForm';

// Importar o novo AdminBookingForm
import { AdminBookingForm } from '../components/admin/booking';

// Componente principal (provedor do contexto)
const AdminPanel = () => {
  const { salonId } = useParams();
  
  // Verificar se temos um salonId válido
  if (!salonId) {
    return <Navigate to="/landing" replace />;
  }
  
  return (
    <NavigationProvider initialTab="appointments">
      <AdminPanelRoutes />
    </NavigationProvider>
  );
};

// Componente de rotas que gerencia as diferentes páginas
const AdminPanelRoutes = () => {
  const { salonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/admin/${salonId}/appointments`} replace />} />
      <Route path="/appointments" element={<AdminPanelContent />} />
      <Route path="/services" element={<AdminPanelContent />} />
      <Route path="/professionals" element={<AdminPanelContent />} />
      <Route path="/commission" element={<AdminPanelContent />} />
      <Route path="/confirmations" element={<AdminPanelContent />} />
      <Route path="/settings" element={<AdminPanelContent />} />
      <Route path="/performance" element={<AdminPanelContent />} />
      <Route path="/users" element={<AdminPanelContent />} />
      <Route path="*" element={<Navigate to={`/admin/${salonId}/appointments`} replace />} />
    </Routes>
  );
};

// Componente interno que consome o contexto
const AdminPanelContent = () => {
  const { salonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Usar o hook de navegação para acessar o contexto
  const { activeTab, setActiveTab } = useNavigation();
  
  // Estados para autenticação e controle da UI
  const [salonConfig, setSalonConfig] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  
  // Ajustando para garantir que a data esteja no fuso horário local
  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const [selectedDate, setSelectedDate] = useState(formattedDate); // Data para visualização da agenda
  
  // Estados para a renderização condicional
  const [shouldRenderLoginScreen, setShouldRenderLoginScreen] = useState(false);
  const [shouldRenderLoadingScreen, setShouldRenderLoadingScreen] = useState(false);
  const [shouldRenderErrorScreen, setShouldRenderErrorScreen] = useState(false);
  
  // Estado para controlar o modal de novo agendamento
  const [newAppointmentModalOpen, setNewAppointmentModalOpen] = useState(false);
  
  // Estado para controlar o modal mobile de novo agendamento
  const [mobileAppointmentModalOpen, setMobileAppointmentModalOpen] = useState(false);
  
  // Usar contextos para acessar dados e funções
  const { addAppointment } = useAppointments();
  const { services } = useServices();
  const { professionals: contextProfessionals } = useProfessionals();
  
  // Usar o contexto de usuário
  const { hasPermission, getCurrentRole, currentUser } = useUser();
  
  // Verificar permissão para gerenciar usuários
  const [canManageUsers, setCanManageUsers] = useState(false);
  
  // Efeito para determinar a aba ativa e verificar permissões
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    if (pathParts.length >= 4 && pathParts[1] === 'admin') {
      const currentTab = pathParts[3] || 'appointments';
      if (currentTab !== activeTab) {
        setActiveTab(currentTab);
      }
    }
    
    // Verificar permissões para gerenciar usuários
    const checkPermissions = async () => {
      if (!currentUser) return;
      
      try {
        // Usar a função especializada para verificar permissão de gerenciar usuários
        const hasManageUsersPermission = await canUserManageUsers(currentUser.uid, salonId);
        setCanManageUsers(hasManageUsersPermission);
        
        // Se o usuário está tentando acessar a aba de usuários sem permissão,
        // redirecionar para a aba de agendamentos
        if (activeTab === 'users' && !hasManageUsersPermission) {
          setActiveTab('appointments');
          navigate(`/admin/${salonId}/appointments`);
        }
      } catch (error) {
        console.error('Erro ao verificar permissões:', error);
        setCanManageUsers(false);
      }
    };
    
    checkPermissions();
  }, [location.pathname, activeTab, setActiveTab, hasPermission, getCurrentRole, currentUser, salonId, navigate]);
  
  // Handler para o envio do formulário mobile
  const handleMobileAppointmentSubmit = async (appointmentData) => {
    try {
      console.log('Dados recebidos do formulário:', appointmentData);
      
      // Se os dados já estão no formato esperado pelo Firebase (como vindo do AdminBookingForm)
      if (appointmentData.data && appointmentData.horario && appointmentData.servicos) {
        // Adicionar o agendamento diretamente
        await addAppointment(appointmentData);
        
        // Fechar o modal após sucesso
        setMobileAppointmentModalOpen(false);
        return;
      }
      
      // Código anterior para compatibilidade com o formato antigo
      // Calcular duração total baseada nos serviços selecionados
      let duracaoTotal = 0;
      const servicosDetalhes = [];
      
      // Criar o array de serviços formatado corretamente
      const servicosArray = appointmentData.servicos.map(serviceId => {
        const service = services.find(s => s.id === serviceId);
        if (service) {
          // Adicionar duração + setup + finish time
          const setupTime = service?.setupTime || 0;
          const finishTime = service?.finishTime || 0;
          const duration = service.duracao || 30;
          
          duracaoTotal += (duration + setupTime + finishTime);
          
          // Adicionar detalhes do serviço
          servicosDetalhes.push({
            id: service.id,
            nome: service.nome,
            preco: service.preco,
            duracao: duration
          });
          
          // Formato esperado
          return {
            id: serviceId,
            nome: service.nome,
            valor: service.preco || 0,
            duracao: duration
          };
        }
        return null;
      }).filter(Boolean);
      
      // Formatar o whatsapp (remover formatação)
      const formattedWhatsapp = appointmentData.whatsapp.replace(/\D/g, '').slice(0, 11);
      
      // Garantir que o profissional está no formato correto
      let profissionalFormatado = appointmentData.profissional;
      if (typeof profissionalFormatado === 'string') {
        const profissional = contextProfessionals.find(p => p.id === profissionalFormatado);
        if (profissional) {
          profissionalFormatado = {
            id: profissional.id,
            nome: profissional.nome
          };
        }
      }
      
      // Calcular horário de fim baseado na duração total
      const [hoursStart, minutesStart] = appointmentData.horario.split(':').map(Number);
      const startTime = new Date();
      startTime.setHours(hoursStart, minutesStart, 0, 0);
      
      const endTime = new Date(startTime.getTime() + (duracaoTotal * 60 * 1000));
      const horarioFim = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;
      
      // Determinar o formato correto da data
      let dataFormatada = '';
      let appointmentDate;
      
      if (typeof appointmentData.data === 'string' && appointmentData.data.includes('/')) {
        // Formato brasileiro (DD/MM/YYYY)
        const [day, month, year] = appointmentData.data.split('/').map(Number);
        appointmentDate = new Date(year, month - 1, day);
        dataFormatada = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      } else if (typeof appointmentData.data === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(appointmentData.data)) {
        // Formato ISO (YYYY-MM-DD)
        const [year, month, day] = appointmentData.data.split('-').map(Number);
        appointmentDate = new Date(year, month - 1, day);
        dataFormatada = appointmentData.data;
      } else {
        // Objeto Date
        appointmentDate = appointmentData.data instanceof Date ? appointmentData.data : new Date();
        dataFormatada = `${appointmentDate.getFullYear()}-${String(appointmentDate.getMonth() + 1).padStart(2, '0')}-${String(appointmentDate.getDate()).padStart(2, '0')}`;
      }
      
      // Definir horas, minutos, etc.
      appointmentDate.setHours(hoursStart, minutesStart, 0, 0);
      
      // Verificar se a data é hoje
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const appointmentDay = new Date(appointmentDate);
      appointmentDay.setHours(0, 0, 0, 0);
      const isToday = today.getTime() === appointmentDay.getTime();
      
      // Preparar dados completos
      const appointmentDbData = {
        cliente: appointmentData.cliente,
        whatsapp: formattedWhatsapp,
        email: appointmentData.email || '',
        observacoes: appointmentData.observacoes || '',
        data: dataFormatada,
        dataTimestamp: appointmentDate,
        dataString: appointmentData.dataFormatada || appointmentData.dataString || formatDateBR(appointmentDate),
        horario: appointmentData.horario.substring(0, 5),
        horarioFim: horarioFim,
        profissional: profissionalFormatado,
        servicos: servicosArray,
        servicosIds: appointmentData.servicos,
        servicosDetalhes: servicosDetalhes,
        duracaoTotal: duracaoTotal,
        duracaoEfetiva: duracaoTotal,
        valorTotal: calculateTotalValue(appointmentData.servicos, services),
        // Usar os valores de status do formulário
        confirmado: appointmentData.confirmado !== undefined ? appointmentData.confirmado : isToday,
        pagamentoConfirmado: appointmentData.pagamentoConfirmado || false,
        cancelado: false,
        origem: 'admin',
        deviceType: 'mobile',
        createdAt: new Date(),
      };
      
      // Adicionar o agendamento
      await addAppointment(appointmentDbData);
      
      // Fechar o modal após sucesso
      setMobileAppointmentModalOpen(false);
      
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      alert('Erro ao salvar o agendamento. Tente novamente.');
    }
  };
  
  // Função auxiliar para calcular o valor total
  const calculateTotalValue = (servicosIds, servicos) => {
    if (!servicosIds || servicosIds.length === 0 || !servicos) {
      return 0;
    }
    
    return servicosIds.reduce((total, serviceId) => {
      const service = servicos.find(s => s.id === serviceId);
      const setupTime = service?.setupTime || 0;
      const finishTime = service?.finishTime || 0;
      const duration = service.duracao || 30;
      return total + (duration + setupTime + finishTime);
    }, 0);
  };
  
  // Efeito para verificar autenticação
  useEffect(() => {
    const token = localStorage.getItem('salonAuthToken');
    if (token === 'admin123') {
      setIsAuthenticated(true);
      setShouldRenderLoginScreen(false);
    } else {
      setShouldRenderLoginScreen(true);
    }
  }, []);
  
  // Efeito para atualizar os estados de renderização condicional
  useEffect(() => {
    setShouldRenderLoginScreen(!isAuthenticated);
    setShouldRenderLoadingScreen(loading);
    setShouldRenderErrorScreen(!!error);
  }, [isAuthenticated, loading, error]);
  
  // Autenticação simples
  const handleLogin = (e) => {
    e.preventDefault();
    // Senha de exemplo - em produção, deve usar Firebase Auth
    if (password === 'admin123') {
      setIsAuthenticated(true);
      localStorage.setItem('salonAuthToken', 'admin123');
    } else {
      alert('Senha incorreta');
    }
  };
  
  // Carregar dados básicos do salão quando autenticado
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchSalonData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Verificar se o salão existe
        const configRef = collection(db, 'salons');
        const configQuery = query(configRef, where('id', '==', salonId));
        const configSnapshot = await getDocs(configQuery);
        
        if (configSnapshot.empty) {
          setError('Salão não encontrado.');
          setLoading(false);
          return;
        }
        
        // Obter configuração do salão
        const salonConfig = await getSalonConfig(salonId);
        setSalonConfig(salonConfig);
        
        // Carregar profissionais para o componente de comissões
        const professionalsRef = collection(db, `salons/${salonId}/professionals`);
        const professionalsSnapshot = await getDocs(professionalsRef);
        const professionalsData = professionalsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setProfessionals(professionalsData);
        setLoading(false);
        
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        setError(error.message);
        setLoading(false);
      }
    };
    
    fetchSalonData();
  }, [isAuthenticated, salonId]);
  
  // Conteúdo para tela de login
  const renderLoginScreen = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Painel de Administração
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Digite a senha para acessar o painel de administração do seu salão
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="password" className="sr-only">Senha</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Entrar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
  
  // Conteúdo para tela de carregamento
  const renderLoadingScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      <p className="mt-4 text-gray-600">Carregando...</p>
    </div>
  );
  
  // Conteúdo para tela de erro
  const renderErrorScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Erro! </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    </div>
  );
  
  // Função para tratar o logout
  const handleLogout = () => {
    localStorage.removeItem('salonAuthToken');
    setIsAuthenticated(false);
  };
  
  // Funções para gerenciamento de modais
  const handleNewService = () => {
    console.log('Gerenciamento de serviços agora é feito pelo componente ServiceManagement');
  };
  
  const handleNewProfessional = () => {
    console.log('Gerenciamento de profissionais agora é feito pelo componente ProfessionalManagement');
  };
  
  const pendingCounts = useNotificationCounts();

  // Função para garantir que a data sempre seja string no formato YYYY-MM-DD
  const handleDateChange = (event) => {
    // Verificar se o valor é um objeto de evento ou um objeto Date
    let dateValue;
    
    if (event && event.target && event.target.value) {
      // Evento normal do DateInput
      dateValue = event.target.value;
    } else if (event instanceof Date) {
      // Objeto Date direto
      dateValue = `${event.getFullYear()}-${String(event.getMonth() + 1).padStart(2, '0')}-${String(event.getDate()).padStart(2, '0')}`;
    } else if (typeof event === 'string') {
      // String direta
      dateValue = event;
    } else {
      console.error('Formato de data não reconhecido:', event);
      return;
    }
    
    // Garantir que está no formato YYYY-MM-DD
    setSelectedDate(dateValue);
  };

  // Renderização condicional
  if (shouldRenderLoginScreen) {
    return renderLoginScreen();
  }

  if (shouldRenderLoadingScreen) {
    return renderLoadingScreen();
  }

  if (shouldRenderErrorScreen) {
    return renderErrorScreen();
  }

  // Layout principal - mantendo o design original
  return (
    <div className="flex min-h-screen bg-white">
      {/* Navegação lateral */}
      <AdminNavigation 
        pendingAppointments={pendingCounts?.pendingAppointments ? [1] : []} // Para compatibilidade com a versão antiga
        salonConfig={salonConfig}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        canManageUsers={canManageUsers} // Passar a permissão para o componente de navegação
      />
      
      {/* Conteúdo principal com margem à esquerda conforme original */}
      <div className="flex-1 flex flex-col md:ml-20 lg:ml-56">
        <AdminHeader
          setNewAppointmentModalOpen={setNewAppointmentModalOpen}
          handleLogout={handleLogout}
        />
        
        <main className="flex-1 overflow-y-auto pb-20 md:pb-0">
          <div className="max-w-7xl mx-auto py-0 md:py-6 lg:px-8">
            {/* Conteúdo das abas - mesma renderização condicional do original */}
            {activeTab === 'appointments' && (
              <AppointmentManagement
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
              />
            )}
            
            {/* Serviços - mantido para compatibilidade, mas não aparece na navegação antiga */}
            {activeTab === 'services' && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium">Gerenciamento de Serviços</h2>
                <ServiceManagement salonId={salonId} />
              </div>
            )}
            
            {/* Profissionais - mantido para compatibilidade, mas não aparece na navegação antiga */}
            {activeTab === 'professionals' && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium">Profissionais</h2>
                <ProfessionalManagement />
              </div>
            )}
            
            {activeTab === 'commission' && (
              <div className="space-y-6 mt-6">
                {professionals && professionals.length > 0 ? (
                  <CommissionManager
                    salonId={salonId}
                    professionals={professionals}
                  />
                ) : (
                  <div className="bg-white shadow rounded-lg p-6 text-center">
                    <p className="text-gray-500">Você precisa cadastrar profissionais antes de gerenciar comissões.</p>
                    <button 
                      onClick={() => {
                        setActiveTab('professionals');
                        navigate(`/admin/${salonId}/professionals`);
                      }}
                      className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    >
                      Ir para Profissionais
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'confirmations' && (
              <ConfirmationsPage />
            )}
            
            {activeTab === 'settings' && (
              <SettingsManagementWithCache 
                salonId={salonId}
                salonConfig={salonConfig}
                setSalonConfig={setSalonConfig}
              />
            )}
            
            {activeTab === 'performance' && (
              <PerformancePage salonId={salonId} />
            )}
            
            {/* Nova aba de gerenciamento de usuários */}
            {activeTab === 'users' && canManageUsers && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium">Gerenciamento de Usuários</h2>
                <UserManagement />
                <InvitationManager salonId={salonId} />
              </div>
            )}
          </div>
        </main>
      </div>
      
      {/* Botão flutuante para ações em mobile */}
      <AdminFloatingActionButton 
        setNewAppointmentModalOpen={setNewAppointmentModalOpen}
        setMobileAppointmentModalOpen={setMobileAppointmentModalOpen}
        setNewServiceModalOpen={handleNewService}
        setNewProfessionalModalOpen={handleNewProfessional}
      />
      
      {/* Modal de agendamento para mobile - usando o novo AdminBookingForm */}
      <AdminBookingForm
        isOpen={mobileAppointmentModalOpen}
        onClose={() => setMobileAppointmentModalOpen(false)}
        onSubmit={handleMobileAppointmentSubmit}
        services={services || []}
        professionals={contextProfessionals || []}
        salonId={salonId}
        mode="new"
      />
    </div>
  );
};

// Componente SettingsManagementWithCache mantido como no original
const SettingsManagementWithCache = ({ salonId, salonConfig, setSalonConfig }) => {
  // Verificar se o email do salão está na lista de autorizados para relatórios detalhados
  const authorizedEmails = ['brunnovert98@gmail.com', 'teste@gmeio.com'];
  const isAdmin = salonConfig?.email && authorizedEmails.includes(salonConfig.email.toLowerCase());
  
  return (
    <div>
      <SettingsManagement 
        salonId={salonId} 
        salonEmail={salonConfig?.email || ''} 
        isAdmin={isAdmin}
        salonConfig={salonConfig} 
        setSalonConfig={setSalonConfig} 
      />
    </div>
  );
};

export default AdminPanel; 